import { useAccount } from '@lib/context/account-context'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text
} from '@chakra-ui/react'
import { useState } from 'react'
import Input from '@modules/common/components/input'
import Button from '@modules/common/components/button'
import { useAlert } from '@common/hooks'
import { AVOCADO_CONSTANT } from '@lib/constants'
import { handleAuthError } from '@lib/util/handle-error'
import { medusaRequest } from '@lib/config'

interface AvocadoModalProps {
  isOpen: boolean
  onCloseHandler: () => void
  onRedeem: (pin: string, setRedeeming: (input: boolean) => void) => void
}

const AvocadoPin = ({
  isOpen,
  onCloseHandler,
  onRedeem
}: AvocadoModalProps) => {
  const { setAvocado, customer } = useAccount()

  const [pin, setPin] = useState('')
  const [error, setError] = useState<string>('')
  const [redeeming, setRedeeming] = useState<boolean>(false)
  const [sending, setSending] = useState<boolean>(false)
  const [isForgot, setIsForgot] = useState<boolean>(false)

  const { errorToast, successToast } = useAlert()

  const onCloseModal = () => {
    onCloseHandler()
  }

  const handleRedeem = () => {
    if (isForgot === true) {
      handleForgetPassword()
    } else {
      setError('')
      setRedeeming(true)
      if (!pin) {
        setError('Please enter pin code to redeem')
        setRedeeming(false)
        return
      }

      if (
        (pin as string).length !== 4 ||
        (pin as string).match(/[0-9]{4}/g) === null
      ) {
        setError('Pin must be 4 digits')
        setRedeeming(false)
        return
      }

      setAvocado((pre: any) => {
        return {
          ...pre,
          pin: pin
        }
      })
      onRedeem(pin, setRedeeming)
    }
  }

  const handleForgetPassword = async () => {
    setSending(true)

    if (customer != null && customer.phone != null) {
      const { parsePhoneNumberFromString } = await import('libphonenumber-js')

      const parsedNumber = parsePhoneNumberFromString(
        customer?.phone?.indexOf('+') != -1
          ? customer?.phone
          : '+' + customer?.phone
      )

      if (parsedNumber != null) {
        const { countryCallingCode, nationalNumber } = parsedNumber
        medusaRequest('POST', AVOCADO_CONSTANT.forgot_password, {
          countryCallingCode,
          phoneNumber: nationalNumber
        })
          .then(() => {
            setIsForgot(false)
            setSending(false)
            successToast({
              title: 'Sent Successfully',
              description: `A new Pin has been sent to your phone`
            })
          })
          .catch((error) => {
            setSending(false)
            handleAuthError(error, (message) => {
              errorToast({
                title: 'Error',
                description: message
              })
            })
          })
      } else {
        setSending(false)
      }
    } else {
      setSending(false)
      errorToast({
        title: 'Error',
        description: 'Unauthorized'
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size={'xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isForgot ? 'Forget pin code' : 'Enter your advocado pin code'}
        </ModalHeader>
        <ModalCloseButton></ModalCloseButton>
        <ModalBody py={5}>
          {isForgot && <Text>A new pin code has been sent to your phone</Text>}
          {!isForgot && (
            <Input
              name="pin"
              type="password"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value)
              }}
              label={isForgot ? 'New pin code' : 'Pin code'}
            />
          )}
          {error && <div style={{ color: 'red' }}>{error}</div>}
          <Text
            display={'inline-block'}
            mt={2}
            cursor={'pointer'}
            textDecoration={'underline'}
            onClick={() => {
              setIsForgot(!isForgot)
            }}
          >
            {isForgot ? 'Back' : 'Forgot Pin?'}
          </Text>
          <Button
            className="float-right mt-6"
            variant="primary"
            onClick={handleRedeem}
            isLoading={redeeming || sending}
            disabled={redeeming || sending}
          >
            {isForgot ? 'Send' : 'Redeem'}
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default AvocadoPin
